import { LocaleContext } from '../../../components/LocaleContext';
import React, { useContext, useRef } from "react";
import { DatabaseContext } from "../../../redux/DatabaseStore";
import { postRequest } from "../../../api/ApiController";
import AuthContext from "../../../components/AuthContext";
import UtilContext from "../../../components/UtilContext";
import { InputNumber } from "primereact/inputnumber";

function getSetting(module_settings, key, fallback) {
  const row = (module_settings || []).find(
    (s) => s.module === "otk" && s.key === key
  );
  return row ? row.value : fallback;
}

export default function OtkStatisticsSettings() {
    const { t } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const { toast } = useContext(UtilContext);

  const module_settings = state.module_settings || [];

  const ordersPctRaw = getSetting(
    module_settings,
    "trend_claim_orders_percent",
    "2"
  );
  const ordersCountRaw = getSetting(
    module_settings,
    "trend_claim_orders_count",
    "80"
  );
  const partsPctRaw = getSetting(
    module_settings,
    "trend_claim_parts_percent",
    "2"
  );
  const partsCountRaw = getSetting(
    module_settings,
    "trend_claim_parts_count",
    "20"
  );
  const costPctRaw = getSetting(
    module_settings,
    "trend_repair_costs_percent",
    "0.3"
  );
  const costValueRaw = getSetting(
    module_settings,
    "trend_repair_costs_value",
    "20000"
  );

  const settingOrdersPct = module_settings.find(
    (s) => s.module === "otk" && s.key === "trend_claim_orders_percent"
  );
  const settingOrdersCount = module_settings.find(
    (s) => s.module === "otk" && s.key === "trend_claim_orders_count"
  );
  const settingPartsPct = module_settings.find(
    (s) => s.module === "otk" && s.key === "trend_claim_parts_percent"
  );
  const settingPartsCount = module_settings.find(
    (s) => s.module === "otk" && s.key === "trend_claim_parts_count"
  );
  const settingCostPct = module_settings.find(
    (s) => s.module === "otk" && s.key === "trend_repair_costs_percent"
  );
  const settingCostValue = module_settings.find(
    (s) => s.module === "otk" && s.key === "trend_repair_costs_value"
  );

  const parseNumber = (raw, fallback) => {
    if (raw == null) return fallback;
    const normalized = String(raw).replace(",", ".").trim();
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : fallback;
  };

  const ordersPct = parseNumber(ordersPctRaw, 2);
  const ordersCount = parseNumber(ordersCountRaw, 80);
  const partsPct = parseNumber(partsPctRaw, 2);
  const partsCount = parseNumber(partsCountRaw, 20);
  const costPct = parseNumber(costPctRaw, 0.3);
  const costValue = parseNumber(costValueRaw, 20000);

  const showToast = (severity, summary, detail) => {
    toast?.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  async function updateSetting(settingRow, nextValue) {
    if (!settingRow) return;
    const val = String(nextValue);
    if (val === String(settingRow.value ?? "")) return;
    try {
      await postRequest(
        `/api/module_settings/update/${settingRow.id}`,
        { value: val },
        true
      );
      dispatch({
        type: "update_module_setting",
        id: settingRow.id,
        value: val,
      });
      showToast("info", t('Common.Confirmed'), t('Common.ValueChanged'));
    } catch (e) {
      showToast(
        "error",
        t('Common.Error'),
        e?.message || t('Common.SettingsSaveFailed')
      );
    }
  }

  async function changeOrdersPct(value) {
    if (!settingOrdersPct) return;
    const n = parseNumber(value, ordersPct);
    const clamped = Math.max(0, Math.min(100, n));
    await updateSetting(settingOrdersPct, clamped);
  }

  async function changeOrdersCount(value) {
    if (!settingOrdersCount) return;
    const n = parseNumber(value, ordersCount);
    const clamped = Math.max(0, Math.round(n));
    await updateSetting(settingOrdersCount, clamped);
  }

  async function changePartsPct(value) {
    if (!settingPartsPct) return;
    const n = parseNumber(value, partsPct);
    const clamped = Math.max(0, Math.min(100, n));
    await updateSetting(settingPartsPct, clamped);
  }

  async function changePartsCount(value) {
    if (!settingPartsCount) return;
    const n = parseNumber(value, partsCount);
    const clamped = Math.max(0, Math.round(n));
    await updateSetting(settingPartsCount, clamped);
  }

  async function changeCostPct(value) {
    if (!settingCostPct) return;
    const n = parseNumber(value, costPct);
    const clamped = Math.max(0, Math.min(100, n));
    await updateSetting(settingCostPct, clamped);
  }

  async function changeCostValue(value) {
    if (!settingCostValue) return;
    const n = parseNumber(value, costValue);
    const clamped = Math.max(0, Math.round(n));
    await updateSetting(settingCostValue, clamped);
  }

  return (
    <div className="relative flex flex-col h-[calc(100vh-137px)] bg-gray-100 overflow-y-auto overflow-x-hidden">
      <div className="p-4 w-full">
        <h1 className="text-3xl font-semibold text-left text-zinc-900 mb-4">{t('Ui.R67.NASTAVENIA_KONTROLY_KVALITY_STATISTIKY')}</h1>
        <div className="p-6 bg-white rounded-md shadow-md w-full max-w-3xl">
          <section>
            <h2 className="text-lg font-medium text-zinc-800 mb-3 pb-2 border-b border-gray-200">{t('Ui.R67.Limity_pre_graf_Trend_chybovosti_v_case')}</h2>
            <p className="text-sm text-gray-500 mb-5">{t('Ui.R67.Pripustne_hodnoty_pre_zobrazenie_trendu_reklamacii_a_naklado')}</p>

            <div className="mb-6">
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('Ui.R67.Reklamovane_zakazky')}</h3>
              <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
                <div className="flex flex-nowrap items-center gap-3">
                  <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Pripustna_hodnota')}</label>
                  <InputNumber
                    value={ordersPct}
                    onValueChange={(e) => changeOrdersPct(e.value)}
                    min={0}
                    max={100}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    suffix=" %"
                    showButtons
                    className="border border-gray-300 rounded-md pl-2"
                    size={6}
                  />
                </div>
                <div className="flex flex-nowrap items-center gap-3">
                  <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Pripustna_hodnota_pocet_zakaziek')}</label>
                  <InputNumber
                    value={ordersCount}
                    onValueChange={(e) => changeOrdersCount(e.value)}
                    min={0}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    showButtons
                    className="border border-gray-300 rounded-md pl-2"
                    size={6}
                  />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('Ui.R67.Reklamovane_kusy')}</h3>
              <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
                <div className="flex flex-nowrap items-center gap-3">
                  <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Pripustna_hodnota')}</label>
                  <InputNumber
                    value={partsPct}
                    onValueChange={(e) => changePartsPct(e.value)}
                    min={0}
                    max={100}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    suffix=" %"
                    showButtons
                    className="border border-gray-300 rounded-md pl-2"
                    size={6}
                  />
                </div>
                <div className="flex flex-nowrap items-center gap-3">
                  <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Pripustna_hodnota_pocet_kusov')}</label>
                  <InputNumber
                    value={partsCount}
                    onValueChange={(e) => changePartsCount(e.value)}
                    min={0}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    showButtons
                    className="border border-gray-300 rounded-md pl-2"
                    size={6}
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-zinc-700 mb-2">{t('OTKEvaluation.NakladyNaOpravy')}</h3>
              <div className="pl-4 flex flex-col gap-3 border-l-2 border-gray-200">
                <div className="flex flex-nowrap items-center gap-3">
                  <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Pripustna_hodnota')}</label>
                  <InputNumber
                    value={costPct}
                    onValueChange={(e) => changeCostPct(e.value)}
                    min={0}
                    max={100}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    suffix=" %"
                    showButtons
                    className="border border-gray-300 rounded-md pl-2"
                    size={6}
                  />
                </div>
                <div className="flex flex-nowrap items-center gap-3">
                  <label className="text-sm font-medium text-gray-800 min-w-0 flex-1">{t('Ui.R67.Pripustna_hodnota_suma')}</label>
                  <InputNumber
                    value={costValue}
                    onValueChange={(e) => changeCostValue(e.value)}
                    min={0}
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={0}
                    prefix="€ "
                    showButtons
                    className="border border-gray-300 rounded-md pl-2"
                    size={6}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

